import Cookies from "js-cookie";
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { getAuthHeaders } from "../utils/authHeaders";
import { countDistinctActiveTradeMatches } from "../utils/countDistinctActiveTradeMatches";

const MY_GAMES_PREFETCH_DELAY_MS = 600;

/** Single TTL for My Games local caches and Header `c8_games_count` fallback (keep aligned). */
export const MY_GAMES_CLIENT_CACHE_TTL_MS = 90_000;

const MY_ACTIVE_GAMES_CACHE_KEY = "c8_my_active_games";
const MY_ACTIVE_GAMES_CACHE_TTL_MS = MY_GAMES_CLIENT_CACHE_TTL_MS;
const MY_ACTIVE_TRADER_CACHE_KEY = "c8_my_active_trader";
const MY_ACTIVE_SQUARES_CACHE_KEY = "c8_my_active_squares";
const MY_GAMES_OVERVIEW_CACHE_KEY = "c8_my_games_overview";
const MY_GAMES_OVERVIEW_CACHE_TTL_MS = MY_GAMES_CLIENT_CACHE_TTL_MS;
const MY_GAMES_OVERVIEW_TRADER_CACHE_KEY = "c8_my_games_overview_trader";
const MY_GAMES_OVERVIEW_SQUARES_CACHE_KEY = "c8_my_games_overview_squares";

/** After `quickfireendgame` succeeds: My Games page listens to refetch history; Header uses `gamesCountUpdate`. */
export const QUICKFIRE_ENDGAME_SYNCED_EVENT = "quickfireEndgameSynced";

/**
 * Optional `detail` for `CustomEvent("gamesCountUpdate", { detail })`.
 * If `bypassCache` is not `false`, listeners treat it like a hard refresh (invalidate flows, new trades, etc.).
 * Use `{ bypassCache: false }` when the server is unlikely to have changed active-game rows (e.g. wallet-only updates).
 */
export type GamesCountUpdateDetail = {
    bypassCache?: boolean;
};

/** Clear overview/active My Games caches so the next fetch hits the API (e.g. after Quickfire session written to `quickfire_plays`). */
export function invalidateMyGamesFetchCaches(): void {
    try {
        localStorage.removeItem(MY_ACTIVE_GAMES_CACHE_KEY);
        localStorage.removeItem(MY_ACTIVE_TRADER_CACHE_KEY);
        localStorage.removeItem(MY_ACTIVE_SQUARES_CACHE_KEY);
        localStorage.removeItem(MY_GAMES_OVERVIEW_CACHE_KEY);
        localStorage.removeItem(MY_GAMES_OVERVIEW_TRADER_CACHE_KEY);
        localStorage.removeItem(MY_GAMES_OVERVIEW_SQUARES_CACHE_KEY);
        localStorage.removeItem("c8_games_count");
    } catch {
        // ignore
    }
}

/** Dispatched when squares have been merged into active games cache (so drawer etc. can refresh squares). */
export const MY_ACTIVE_GAMES_SQUARES_UPDATED = "myActiveGamesSquaresUpdated";

export interface MyGamesActivePayload {
    trades: unknown[];
    squares: unknown[];
    /** In-progress Quickfire sessions (`quickfire_plays` latest snapshot per session). */
    quickfire?: unknown[];
    totalActiveGames: number;
    /** Active in-progress BULLION plays (from `/trade/my-games-overview`); folded into `totalActiveGames`. */
    bullionActivePlaysCount?: number;
    /** Active in-progress Sixes cards (from `/trade/my-games-overview`); folded into `totalActiveGames`. */
    sixesActiveCardsCount?: number;
    /** True when squares are still loading (trader returned first); consumer should not clear squares. */
    squaresPending?: boolean;
}

export interface MyGamesSettledTodayPayload {
    trades: unknown[];
    squares: unknown[];
}

export interface MyGamesOverviewPayload {
    active: MyGamesActivePayload;
    settledToday: MyGamesSettledTodayPayload;
}

export type FetchMyGamesOpts = { bypassCache?: boolean };

interface TraderDataFetcherContextProps {
    fetchMyGamesActive: (opts?: FetchMyGamesOpts) => Promise<MyGamesActivePayload>;
    getCachedMyGamesActive: () => MyGamesActivePayload | null;
    fetchMyGamesOverview: (opts?: FetchMyGamesOpts) => Promise<MyGamesOverviewPayload>;
    getCachedMyGamesOverview: () => MyGamesOverviewPayload | null;
}

const TraderDataFetcherContext = createContext<TraderDataFetcherContextProps | undefined>(undefined);

export const TraderDataFetcherProvider = ({ children }: { children: ReactNode }) => {
    const overviewInFlightRef = useRef<Promise<MyGamesOverviewPayload> | null>(null);

    const getCachedMyGamesActive = useMemo(() => (): MyGamesActivePayload | null => {
        try {
            const raw = localStorage.getItem(MY_ACTIVE_GAMES_CACHE_KEY);
            if (raw) {
                const {
                    trades,
                    squares,
                    quickfire,
                    totalActiveGames,
                    bullionActivePlaysCount,
                    sixesActiveCardsCount,
                    timestamp,
                } = JSON.parse(raw);
                // Older caches folded Sixes into `totalActiveGames` but omitted `sixesActiveCardsCount`,
                // which left the burger SIXES badge at 0 while MY GAMES showed the total. Drop them
                // (including split trader/squares keys) so the next fetch rewrites a complete payload.
                if (sixesActiveCardsCount === undefined) {
                    localStorage.removeItem(MY_ACTIVE_GAMES_CACHE_KEY);
                    localStorage.removeItem(MY_ACTIVE_TRADER_CACHE_KEY);
                    localStorage.removeItem(MY_ACTIVE_SQUARES_CACHE_KEY);
                    localStorage.removeItem(MY_GAMES_OVERVIEW_CACHE_KEY);
                    localStorage.removeItem(MY_GAMES_OVERVIEW_TRADER_CACHE_KEY);
                    localStorage.removeItem(MY_GAMES_OVERVIEW_SQUARES_CACHE_KEY);
                    return null;
                }
                if (Date.now() - (timestamp || 0) <= MY_ACTIVE_GAMES_CACHE_TTL_MS) {
                    const qf = Array.isArray(quickfire) ? quickfire : [];
                    const bullion = Math.max(0, parseInt(String(bullionActivePlaysCount ?? 0), 10) || 0);
                    const sixes = Math.max(0, parseInt(String(sixesActiveCardsCount ?? 0), 10) || 0);
                    return {
                        trades: Array.isArray(trades) ? trades : [],
                        squares: Array.isArray(squares) ? squares : [],
                        quickfire: qf,
                        bullionActivePlaysCount: bullion,
                        sixesActiveCardsCount: sixes,
                        totalActiveGames:
                            typeof totalActiveGames === "number"
                                ? totalActiveGames
                                : countDistinctActiveTradeMatches(Array.isArray(trades) ? trades : []) +
                                  (Array.isArray(squares) ? squares.length : 0) +
                                  qf.length +
                                  bullion +
                                  sixes,
                    };
                }
            }
            const traderRaw = localStorage.getItem(MY_ACTIVE_TRADER_CACHE_KEY);
            const squaresRaw = localStorage.getItem(MY_ACTIVE_SQUARES_CACHE_KEY);
            if (traderRaw && squaresRaw) {
                const trader = JSON.parse(traderRaw);
                const squares = JSON.parse(squaresRaw);
                if (Date.now() - (trader.timestamp || 0) <= MY_ACTIVE_GAMES_CACHE_TTL_MS && Date.now() - (squares.timestamp || 0) <= MY_ACTIVE_GAMES_CACHE_TTL_MS) {
                    const trades = Array.isArray(trader.trades) ? trader.trades : [];
                    const sq = Array.isArray(squares.squares?.data) ? squares.squares.data : (Array.isArray(squares.squares) ? squares.squares : []);
                    const qf = Array.isArray(trader.quickfire) ? trader.quickfire : [];
                    return {
                        trades,
                        squares: sq,
                        quickfire: qf,
                        bullionActivePlaysCount: 0,
                        sixesActiveCardsCount: 0,
                        totalActiveGames:
                            countDistinctActiveTradeMatches(trades) + (squares.squares?.count ?? sq.length) + qf.length,
                    };
                }
            }
            return null;
        } catch {
            return null;
        }
    }, []);

    const getCachedMyGamesOverview = useMemo(() => (): MyGamesOverviewPayload | null => {
        try {
            const raw = localStorage.getItem(MY_GAMES_OVERVIEW_CACHE_KEY);
            if (raw) {
                const { active, settledToday, timestamp } = JSON.parse(raw);
                if (active && active.sixesActiveCardsCount === undefined) {
                    localStorage.removeItem(MY_ACTIVE_GAMES_CACHE_KEY);
                    localStorage.removeItem(MY_ACTIVE_TRADER_CACHE_KEY);
                    localStorage.removeItem(MY_ACTIVE_SQUARES_CACHE_KEY);
                    localStorage.removeItem(MY_GAMES_OVERVIEW_CACHE_KEY);
                    localStorage.removeItem(MY_GAMES_OVERVIEW_TRADER_CACHE_KEY);
                    localStorage.removeItem(MY_GAMES_OVERVIEW_SQUARES_CACHE_KEY);
                    return null;
                }
                if (Date.now() - (timestamp || 0) <= MY_GAMES_OVERVIEW_CACHE_TTL_MS && active && settledToday) {
                    const cachedQf = Array.isArray(active.quickfire) ? active.quickfire : [];
                    const bullion = Math.max(0, parseInt(String(active.bullionActivePlaysCount ?? 0), 10) || 0);
                    const sixes = Math.max(0, parseInt(String(active.sixesActiveCardsCount ?? 0), 10) || 0);
                    return {
                        active: {
                            trades: Array.isArray(active.trades) ? active.trades : [],
                            squares: Array.isArray(active.squares) ? active.squares : [],
                            quickfire: cachedQf,
                            bullionActivePlaysCount: bullion,
                            sixesActiveCardsCount: sixes,
                            totalActiveGames:
                                typeof active.totalActiveGames === "number"
                                    ? active.totalActiveGames
                                    : countDistinctActiveTradeMatches(Array.isArray(active.trades) ? active.trades : []) +
                                      (Array.isArray(active.squares) ? active.squares.length : 0) +
                                      cachedQf.length +
                                      bullion +
                                      sixes,
                        },
                        settledToday: {
                            trades: Array.isArray(settledToday.trades) ? settledToday.trades : [],
                            squares: Array.isArray(settledToday.squares) ? settledToday.squares : [],
                        },
                    };
                }
            }
            const traderRaw = localStorage.getItem(MY_GAMES_OVERVIEW_TRADER_CACHE_KEY);
            const squaresRaw = localStorage.getItem(MY_GAMES_OVERVIEW_SQUARES_CACHE_KEY);
            if (traderRaw && squaresRaw) {
                const trader = JSON.parse(traderRaw);
                const squares = JSON.parse(squaresRaw);
                if (Date.now() - (trader.timestamp || 0) <= MY_GAMES_OVERVIEW_CACHE_TTL_MS && Date.now() - (squares.timestamp || 0) <= MY_GAMES_OVERVIEW_CACHE_TTL_MS) {
                    const activeTrades = Array.isArray(trader.active?.trades) ? trader.active.trades : [];
                    const activeSq = Array.isArray(squares.active?.squares?.data) ? squares.active.squares.data : (Array.isArray(squares.active?.squares) ? squares.active.squares : []);
                    const settledTrades = Array.isArray(trader.settledToday?.trades) ? trader.settledToday.trades : [];
                    const settledSq = Array.isArray(squares.settledToday?.squares?.data) ? squares.settledToday.squares.data : (Array.isArray(squares.settledToday?.squares) ? squares.settledToday.squares : []);
                    const splitQf = Array.isArray(trader.active?.quickfire) ? trader.active.quickfire : [];
                    return {
                        active: {
                            trades: activeTrades,
                            squares: activeSq,
                            quickfire: splitQf,
                            bullionActivePlaysCount: 0,
                            sixesActiveCardsCount: 0,
                            totalActiveGames:
                                countDistinctActiveTradeMatches(activeTrades) +
                                (squares.active?.squares?.count ?? activeSq.length) +
                                splitQf.length,
                        },
                        settledToday: {
                            trades: settledTrades,
                            squares: settledSq,
                        },
                    };
                }
            }
            return null;
        } catch {
            return null;
        }
    }, []);

    const fetchMyGamesOverview = useMemo(
        () =>
            async (opts?: FetchMyGamesOpts): Promise<MyGamesOverviewPayload> => {
            const emptyPayload: MyGamesOverviewPayload = {
                active: {
                    trades: [],
                    squares: [],
                    quickfire: [],
                    bullionActivePlaysCount: 0,
                    sixesActiveCardsCount: 0,
                    totalActiveGames: 0,
                },
                settledToday: { trades: [], squares: [] },
            };

            const sessionToken = Cookies.get("c8_session_token");
            if (!sessionToken) {
                return emptyPayload;
            }
            if (!opts?.bypassCache) {
                const cached = getCachedMyGamesOverview();
                if (cached) return cached;
                if (overviewInFlightRef.current) return overviewInFlightRef.current;
            }

            const headers: HeadersInit = getAuthHeaders();
            const apiUrl = process.env.API_URL;

            const run = async (): Promise<MyGamesOverviewPayload> => {
            try {
                const res = await fetch(`${apiUrl}/trade/my-games-overview/${sessionToken}`, { method: "GET", headers });
                if (!res.ok) {
                    const cached = getCachedMyGamesOverview();
                    return cached ?? emptyPayload;
                }
                const data = await res.json();
                const now = Date.now();
                const activeTrades = Array.isArray(data?.active?.trades) ? data.active.trades : [];
                const activeSquaresData = Array.isArray(data?.active?.squares?.data) ? data.active.squares.data : (Array.isArray(data?.active?.squares) ? data.active.squares : []);
                const activeSquaresCount = data?.active?.squares?.count ?? activeSquaresData.length;
                const activeQuickfireData = Array.isArray(data?.active?.quickfire?.data)
                    ? data.active.quickfire.data
                    : (Array.isArray(data?.active?.quickfire) ? data.active.quickfire : []);
                const activeQuickfireCount = data?.active?.quickfire?.count ?? activeQuickfireData.length;
                const bullionActivePlaysCount = Math.max(0, parseInt(String(data?.active?.bullionActivePlaysCount ?? 0), 10) || 0);
                const sixesActiveCardsCount = Math.max(0, parseInt(String(data?.active?.sixesActiveCardsCount ?? 0), 10) || 0);
                const totalActiveGames =
                    typeof data?.active?.totalActiveGames === "number"
                        ? data.active.totalActiveGames
                        : countDistinctActiveTradeMatches(activeTrades) +
                          activeSquaresCount +
                          activeQuickfireCount +
                          bullionActivePlaysCount +
                          sixesActiveCardsCount;
                const settledTodayTrades = Array.isArray(data?.settledToday?.trades) ? data.settledToday.trades : [];
                const settledTodaySquares = Array.isArray(data?.settledToday?.squares?.data) ? data.settledToday.squares.data : (Array.isArray(data?.settledToday?.squares) ? data.settledToday.squares : []);

                const active: MyGamesActivePayload = {
                    trades: activeTrades,
                    squares: activeSquaresData,
                    quickfire: activeQuickfireData,
                    bullionActivePlaysCount,
                    sixesActiveCardsCount,
                    totalActiveGames,
                };
                const settledToday: MyGamesSettledTodayPayload = {
                    trades: settledTodayTrades,
                    squares: settledTodaySquares,
                };
                const payload: MyGamesOverviewPayload = { active, settledToday };

                const prevCached = getCachedMyGamesOverview();
                const prevCount = prevCached?.active?.totalActiveGames;
                const prevBullion = prevCached?.active?.bullionActivePlaysCount ?? 0;
                const prevSixes = prevCached?.active?.sixesActiveCardsCount ?? 0;
                const prevSquaresLen = prevCached?.active?.squares?.length ?? 0;
                const breakdownChanged =
                    prevCount !== totalActiveGames ||
                    prevBullion !== bullionActivePlaysCount ||
                    prevSixes !== sixesActiveCardsCount;

                try {
                    localStorage.setItem(
                        MY_GAMES_OVERVIEW_TRADER_CACHE_KEY,
                        JSON.stringify({
                            active: {
                                trades: activeTrades,
                                quickfire: activeQuickfireData,
                                totalActiveGames:
                                    countDistinctActiveTradeMatches(activeTrades) + activeQuickfireCount,
                            },
                            settledToday: { trades: settledTodayTrades },
                            timestamp: now,
                        })
                    );
                    localStorage.setItem(MY_GAMES_OVERVIEW_SQUARES_CACHE_KEY, JSON.stringify({ active: { squares: { data: activeSquaresData, count: activeSquaresCount } }, settledToday: { squares: settledTodaySquares }, timestamp: now }));
                    localStorage.setItem(
                        MY_ACTIVE_TRADER_CACHE_KEY,
                        JSON.stringify({
                            trades: activeTrades,
                            quickfire: activeQuickfireData,
                            totalActiveGames:
                                countDistinctActiveTradeMatches(activeTrades) + activeQuickfireCount,
                            timestamp: now,
                        })
                    );
                    localStorage.setItem(MY_ACTIVE_SQUARES_CACHE_KEY, JSON.stringify({ squares: { data: activeSquaresData, count: activeSquaresCount }, timestamp: now }));
                    localStorage.setItem(MY_GAMES_OVERVIEW_CACHE_KEY, JSON.stringify({ ...payload, timestamp: now }));
                    localStorage.setItem(MY_ACTIVE_GAMES_CACHE_KEY, JSON.stringify({ ...active, timestamp: now }));
                    localStorage.setItem("c8_games_count", JSON.stringify({ count: totalActiveGames, timestamp: now }));
                    // Fire even when only per-game breakdown changes (same total) so burger badges refresh.
                    if (breakdownChanged) {
                        window.dispatchEvent(new CustomEvent("gamesCountUpdated", { detail: { count: totalActiveGames } }));
                    }
                    if (activeSquaresData.length !== prevSquaresLen) {
                        window.dispatchEvent(new CustomEvent(MY_ACTIVE_GAMES_SQUARES_UPDATED));
                    }
                } catch (e) {
                    console.error("Error updating overview cache:", e);
                }
                return payload;
            } catch {
                const cached = getCachedMyGamesOverview();
                return cached ?? emptyPayload;
            }
            };

            if (!opts?.bypassCache) {
                const pending = run().finally(() => {
                    overviewInFlightRef.current = null;
                });
                overviewInFlightRef.current = pending;
                return pending;
            }
            return run();
        },
        [getCachedMyGamesOverview],
    );

    const fetchMyGamesActive = useMemo(
        () =>
            async (opts?: FetchMyGamesOpts): Promise<MyGamesActivePayload> => {
            const sessionToken = Cookies.get("c8_session_token");
            if (!sessionToken) {
                return {
                    trades: [],
                    squares: [],
                    quickfire: [],
                    bullionActivePlaysCount: 0,
                    sixesActiveCardsCount: 0,
                    totalActiveGames: 0,
                };
            }

            if (!opts?.bypassCache) {
                const cached = getCachedMyGamesActive();
                if (cached) return cached;
                const overview = getCachedMyGamesOverview();
                if (overview) return overview.active;
            }

            const payload = await fetchMyGamesOverview({ bypassCache: opts?.bypassCache });
            return payload.active;
        },
        [getCachedMyGamesActive, getCachedMyGamesOverview, fetchMyGamesOverview],
    );

    const contextValue = useMemo(
        () => ({ fetchMyGamesActive, getCachedMyGamesActive, fetchMyGamesOverview, getCachedMyGamesOverview }),
        [fetchMyGamesActive, getCachedMyGamesActive, fetchMyGamesOverview, getCachedMyGamesOverview],
    );

    // Overview prefetch only (drawer/header). History is prefetched when My Zone My Games is opened.
    useEffect(() => {
        const sessionToken = Cookies.get("c8_session_token");
        if (!sessionToken) return;

        const timer = setTimeout(() => {
            fetchMyGamesOverview().catch(() => {});
        }, MY_GAMES_PREFETCH_DELAY_MS);

        return () => clearTimeout(timer);
    }, [fetchMyGamesOverview]);

    return <TraderDataFetcherContext.Provider value={contextValue}>{children}</TraderDataFetcherContext.Provider>;
};

export const useTraderDataFetcher = () => {
    const context = useContext(TraderDataFetcherContext);
    if (!context) {
        throw new Error("useTraderDataFetcher must be used within a DataFetcherProvider");
    }
    return context;
};
